import React, { useState, lazy } from 'react';
import { ArrowUpDownIcon, ArrowDownIcon, ArrowUpIcon, InfoIcon, TrophyIcon } from 'lucide-react';
import { ImageData } from '../utils/mockData';
import { Tooltip } from './Tooltip';
interface ImagePerformanceTableProps {
  data: ImageData[];
  segment: 'techEnthusiast' | 'lifestyle';
}
type SortField = 'stoppingPower' | 'views' | 'engagementRate';
type SortDirection = 'asc' | 'desc';
export const ImagePerformanceTable: React.FC<ImagePerformanceTableProps> = ({
  data,
  segment
}) => {
  const [sortField, setSortField] = useState<SortField>('stoppingPower');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const sortedData = [...data].sort((a, b) => {
    const aValue = a[segment][sortField];
    const bValue = b[segment][sortField];
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return <ArrowUpDownIcon className="h-4 w-4 opacity-50" data-id="element-226" />;
    return sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4" data-id="element-227" /> : <ArrowDownIcon className="h-4 w-4" data-id="element-228" />;
  };
  return <div className="bg-white rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#E5E5E5] overflow-hidden" data-id="element-229">
      <div className="overflow-x-auto" data-id="element-230">
        <table className="min-w-full divide-y divide-[#E5E5E5]" data-id="element-231">
          <thead className="bg-[#F9FAFB]" data-id="element-232">
            <tr data-id="element-233">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#737373] uppercase tracking-wider" data-id="element-234">
                Asset
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#737373] uppercase tracking-wider" data-id="element-235">
                <div className="flex items-center gap-1.5" data-id="element-236">
                  SPR
                  <Tooltip content="Stopping Power Rating (SPR) shows how well this image grabs attention by turning impressions into actual views." data-id="element-237" />
                </div>
              </th>

              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#737373] uppercase tracking-wider" data-id="element-238">
                Audience Signals
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#E5E5E5]" data-id="element-239">
            {sortedData.map((item, index) => <tr key={item.id} className={`hover:bg-[#F9FAFB] transition-colors ${index === 0 ? 'bg-[#C00000]/5' : ''}`} data-id="element-240">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-241">
                  <div className="flex items-center" data-id="element-242">
                    <div className="flex items-center gap-2" data-id="element-243">
                      <div className="h-12 w-20 flex-shrink-0 overflow-hidden rounded-none bg-[#F9FAFB]" data-id="element-244">
                        <img src={item.thumbnailUrl} alt={item.name} className="h-full w-full object-cover" referrerPolicy="no-referrer" loading="lazy" data-id="element-245" />
                      </div>
                      <div className="ml-4" data-id="element-246">
                        <div className="text-sm font-medium text-[#101010]" data-id="element-247">
                          {item.name}
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-248">
                  <div className="flex items-center" data-id="element-249">
                    <div className="w-24 bg-gray-100 rounded-none h-2.5" data-id="element-250">
                      <div className="h-2.5 rounded-sm bg-[#C00000]" style={{
                    width: `${item[segment].stoppingPower}%`
                  }} data-id="element-251" />
                    </div>
                    <span className="ml-2 text-sm font-medium text-[#101010]" data-id="element-252">
                      {item[segment].stoppingPower}
                    </span>
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#737373]" data-id="element-253">
                  {item[segment].views.toLocaleString()}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
};